import React, { useState } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import './RouterErrorBoundary.scss';
import { useLelyAuth } from '@/auth/useLelyAuth';
import {
  didAttemptChunkReloadBeforeThisDocument,
  isStaleChunkLoadError,
  tryReloadForStaleChunk,
} from '@/common/utils/staleChunkLoadError';
import { LelyButton } from '@/common/components';
import Preloader from '../Preloader/Preloader';
type Props = {
  children: React.ReactNode;
};

const beautifyError = (error: unknown, email: string) => {
  const normalizedError =
    error instanceof Error ? error : new Error(String(error));
  const formattedStack = (normalizedError.stack ?? 'No stack available')
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n');

  return `Name: ${normalizedError.name ?? 'Error'}
Message: ${normalizedError.message ?? 'Unknown error'}
URL: ${window.location.href}
User: ${email}
Timestamp: ${new Date().toISOString()}

Stack:
${formattedStack}`;
};

function RouterErrorFallback({ error }: FallbackProps) {
  const { user } = useLelyAuth();
  const email = user?.email || '';
  const [showReloadLoader] = useState(
    () =>
      isStaleChunkLoadError(error) &&
      !didAttemptChunkReloadBeforeThisDocument(),
  );
  const handleCopyToClipboardClick = () => {
    navigator.clipboard.writeText(beautifyError(error, email || ''));
  };

  if (showReloadLoader) {
    return <Preloader activeByDefault />;
  }

  return (
    <div className="ltda-error-boundary">
      <p className="ltda-error-boundary__message fs--d-md-default">
        Something went wrong while rendering the components. Please share the
        technical details below with the development team.
      </p>

      <LelyButton
        variant="secondary"
        onClick={handleCopyToClipboardClick}
        data-testid="error-copy-button"
      >
        Copy to clipboard
      </LelyButton>
      <pre className="ltda-error-boundary__details-content fs--d-header">
        {beautifyError(error, email || '')}
      </pre>
    </div>
  );
}

function RouterErrorBoundary({ children }: Props) {
  const location = useLocation();

  return (
    <ErrorBoundary
      FallbackComponent={RouterErrorFallback}
      resetKeys={[location.pathname]}
      onError={(error, info) => {
        if (tryReloadForStaleChunk(error)) {
          console.log('Reloading for stale chunk', error, info);
          return;
        }
        // Keep the rest of the page usable while capturing crash details.
        console.error('Render failed', error, info);
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

export default RouterErrorBoundary;
